'use client';

import { useTalentAuth } from '@/shared/contexts/talent-auth';

/**
 * i18n namespace ("module") that stores the centralized side-menu labels.
 *
 * The "Personalización del menú" UI (Config. General > Apariencia > Comunicacional)
 * writes company-scoped overrides into this exact namespace via the label service,
 * so ANY breadcrumb segment or page title read from here automatically reflects a
 * company's custom module naming — the same source the centralized sidebar uses.
 */
const MENU_LABELS_NAMESPACE = 'common';

/**
 * Canonical label codes for the centralized menu (levels 1 and 2 are personalizable;
 * a few level-3 leaves that already have a stable menu code are included too).
 *
 * These MUST stay in sync with the "Etiquetas menú – Códigos menú" spreadsheet and
 * with the codes the menu-personalization service writes to (module = "common").
 * Homologating Talent breadcrumbs/titles onto these codes means a single label update
 * renames the module everywhere it appears (sidebar, breadcrumb and page title).
 */
export const MENU_LABEL = {
  // ── Home ────────────────────────────────────────────────────────────────
  home: 'menu_home_title',

  // ── Level 1 modules (Talent scope) ──────────────────────────────────────
  analytics: 'menu_analytics_title',
  recognition: 'module_recognition_title',
  performance: 'module_performance_title',
  climate: 'module_engagement_title',
  myTeam: 'menu_my_team',
  myResults: 'menu_my_results',

  // ── Analytics submodules ────────────────────────────────────────────────
  statistics: 'module_stadistics_analytics_title',
  reports: 'module_reports_analytics_title',
  talent: 'talent',
  nbox: 'nbox_module_title',

  // ── Recognition submodules ──────────────────────────────────────────────
  recognitionResults: 'menu_recognition_results',
  medalsRequest: 'admin_menu_medals_request',
  rewards: 'menu_rewards',
  // Recognition-specific configuration code. NOT the generic `configuration`
  // label, which is shared platform-wide — personalizing that one would rename
  // every "Configuración" across the product.
  recognitionConfiguration: 'menu_recognition_configuration',
  medals: 'menu_medals',
  messages: 'admin_menu_messages',
  rulesAndPermissions: 'admin_menu_rules_and_permissions',

  // ── Performance submodules ──────────────────────────────────────────────
  performanceResults: 'module_results_performance_title',
  competences: 'module_competences_title',
  goals: 'module_goals_title',
  okrs: 'module_okrs_title',
  objectives: 'menu_goals',
  alignment: 'menu_alignment',
  feedback: 'module_feedback_title',
  pid: 'module_pid_title',
  successionPlan: 'module_succession_plan_title',

  // ── Climate submodules ──────────────────────────────────────────────────
  climateResults: 'menu_climate_results',
  climateEvaluations: 'module_climate_title',
  shortSurveys: 'admin_menu_short_surveys',
  nom035: 'admin_menu_other_evaluations',

  // ── My team (leader) submodules ─────────────────────────────────────────
  followUp: 'menu_dashboard_leader',
  followUpGeneral: 'menu_dashboard_leader_general',
  followUpByMember: 'menu_dashboard_leader_by_team_member',
  myTeamResults: 'menu_my_team_results',
} as const;

export type TMenuLabelCode = (typeof MENU_LABEL)[keyof typeof MENU_LABEL];

/**
 * Returns a resolver for the centralized menu labels.
 *
 * Use `menuLabel(code, fallback)` for breadcrumb level 1/2 segments and page titles
 * that map to a menu module/submodule. It resolves the name through
 * `useTalentAuth().getModuleName` — the SAME source the centralized sidebar uses
 * (`get_labels_translation`) — so the value always matches what the menu shows,
 * including a company's personalization. Pass the page's current translation as the
 * `fallback` for the (rare) case where the label is not in the module list yet.
 */
export const useMenuLabels = () => {
  const { getModuleName } = useTalentAuth();

  const menuLabel = (code: TMenuLabelCode, fallback?: string) => {
    return getModuleName(`${MENU_LABELS_NAMESPACE}.${code}`) ?? fallback ?? '';
  };

  return { menuLabel };
};
